/**
 * screens/admin/adminAudiences.tsx — N-5 · Audience registry.
 *
 * INTERNAL CONSOLE ONLY. SKPLATFORM-UICC-006 §8 / DB-006 S8.
 *
 * Low traffic, read-mostly, and its job is to make ONE property visible:
 *
 *   Registering a new consumer is a ROW HERE, not a code change.
 *
 * That is the whole argument for extracting customer identity now rather than
 * later. Under the retiring path, every new vertical meant editing Platform's
 * signer and adding an audience string — putting per-vertical, customer-facing
 * change on the most conservatively released service in the estate.
 *
 * A line of explanatory copy at the top is worth more than any control on this
 * screen, so that line is the first thing rendered and there are no controls.
 *
 * Retired audiences STAY LISTED, inactive, struck through. The register is a
 * history as well as a configuration: the interim Fleet audience is
 * deactivated at cutover and deliberately never deleted, so the retired path
 * stays describable and auditable.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { SettingsHeader, Panel, DataTable, Tag, type Column } from '../../components/platform/primitives';
import { PEmpty } from '../../components/platform/controls';
import { FS } from '../../utils/platformTokens';
import { listAppAudiences } from '../../services/platform/platformApi';
import type { AppAudienceRow } from '../../schema/platform/types';

const AdminAudiences: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [rows, setRows] = useState<AppAudienceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try { setRows(await listAppAudiences().catch(() => [])); }
      finally { setLoading(false); }
    })();
  }, []);

  /** Active first, then retired — the register reads as current state with
   *  history beneath it, rather than as an undifferentiated list. */
  const ordered = useMemo(
    () => [...rows].sort((a, b) =>
      a.is_active === b.is_active ? a.code.localeCompare(b.code) : a.is_active ? -1 : 1,
    ),
    [rows],
  );

  const columns: Column<AppAudienceRow>[] = [
    {
      key: 'code',
      header: 'Code',
      width: 180,
      render: (r) => (
        <Text
          selectable
          style={{
            fontSize: FS.sm, color: r.is_active ? c.ink : c.ink4,
            fontFamily: theme.font.mono,
            textDecorationLine: r.is_active ? 'none' : 'line-through',
          }}
        >
          {r.code}
        </Text>
      ),
    },
    {
      key: 'name',
      header: 'Display name',
      width: 260,
      render: (r) => (
        <Text style={{ fontSize: FS.sm, color: r.is_active ? c.ink2 : c.ink4 }}>{r.display_name}</Text>
      ),
    },
    {
      key: 'realm',
      header: 'Realm',
      width: 120,
      render: (r) => (
        /* A staff-realm audience must never appear on a customer token or vice
           versa — checked at MINT time, not at verify. Shown here so the
           pairing is inspectable. */
        <Tag tone={r.realm === 'customer' ? 'warn' : r.realm === 'service' ? 'info' : 'neutral'}>
          {r.realm}
        </Tag>
      ),
    },
    {
      key: 'active',
      header: 'Active',
      width: 110,
      render: (r) =>
        r.is_active
          ? <Tag dot tone="success">active</Tag>
          : <Text style={{ fontSize: FS.xs, color: c.ink4 }}>retired</Text>,
    },
    {
      key: 'desc',
      header: 'Description',
      render: (r) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, lineHeight: 18 }}>{r.description ?? '—'}</Text>
      ),
    },
  ];

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1280, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Audience registry"
        sub="The valid audience values a Platform token may name — Softknack internal."
      />

      {/* The line that is worth more than any control on this screen. */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: FS.sm, color: c.ink2, lineHeight: 20, maxWidth: 760 }}>
          Registering a new consumer — a vertical&apos;s customer app, a staff app — is a row in
          this table, not a change to the service that authenticates every operator administrator
          in the company. That is what keeps per-product work off Platform&apos;s release cadence.
        </Text>
      </View>

      <Panel>
        <DataTable
          columns={columns}
          rows={ordered}
          keyExtractor={(r) => r.code}
          minWidth={1020}
          empty={<PEmpty title={loading ? 'Loading the registry…' : 'No audiences registered'} />}
        />
      </Panel>
    </ScrollView>
  );
};

export default AdminAudiences;
